import { useState, type FormEvent } from 'react';
import { services } from '@/mocks/site';

const ENDPOINT = 'https://readdy.ai/api/form/db6ph84jfl1ikpv8sns0';
const MAX_MSG = 500;

type Status = 'idle' | 'sending' | 'success' | 'error';
type FieldErrors = Partial<Record<'name' | 'email' | 'service' | 'message', string>>;

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: unknown };
}

const fieldBase = 'w-full border-0 border-b bg-transparent px-0 py-3 text-[15px] text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-0';

export default function InquiryForm({ id = 'avenor-inquiry' }: { id?: string }) {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [msgLen, setMsgLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const trap = String(fd.get('company_alt') ?? '').trim();
    if (trap) {
      setStatus('success');
      return;
    }
    fd.delete('company_alt');

    const name = String(fd.get('name') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const service = String(fd.get('service') ?? '').trim();
    const message = String(fd.get('message') ?? '').trim();

    const next: FieldErrors = {};
    if (!name) next.name = 'Please enter your name.';
    if (!email) next.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address.';
    if (!service) next.service = 'Please choose an area of interest.';
    if (!message) next.message = 'Please add a short message.';
    else if (message.length > MAX_MSG) next.message = `Please keep your message under ${MAX_MSG} characters.`;
    setErrors(next);
    if (Object.keys(next).length) {
      setStatus('idle');
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((v, k) => { params.append(k, String(v)); });

    setStatus('sending');
    setFormError('');
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try { parsed = JSON.parse(responseText) as ParsedResponse; } catch { parsed = null; }
      const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
      const serverMsg = parsed?.meta?.message || parsed?.message || detail || responseText;
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg ? `Your message was not sent: ${serverMsg}` : 'Your message was not sent. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Your message could not be sent because of a connection problem. Nothing was submitted — please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div role="status" className="border-t border-foreground-950 pt-8">
        <p className="font-label text-[11px] uppercase tracking-[0.14em] text-primary-500">Received</p>
        <p className="mt-4 text-2xl font-light leading-snug text-foreground-950 md:text-[28px]">Thank you. Your inquiry has been delivered.</p>
        <p className="mt-3 text-[15px] leading-relaxed text-foreground-700">A member of the team will reply by email.</p>
        <button type="button" onClick={() => setStatus('idle')} className="ed-link mt-8 cursor-pointer whitespace-nowrap font-label text-[11px] uppercase tracking-[0.14em] text-foreground-950">
          Send another inquiry
        </button>
      </div>
    );
  }

  const line = (k: keyof FieldErrors) => (errors[k] ? 'border-primary-500' : 'border-background-300 focus:border-foreground-950');

  return (
    <form id={id} data-readdy-form noValidate onSubmit={handleSubmit} className="relative border-t border-foreground-950">
      <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        <div className="pt-6">
          <label htmlFor={`${id}-name`} className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700">Name <span className="text-primary-500">*</span></label>
          <input id={`${id}-name`} name="name" type="text" autoComplete="name" placeholder="Your full name" aria-invalid={!!errors.name} aria-describedby={errors.name ? `${id}-name-err` : undefined} className={`${fieldBase} ${line('name')}`} />
          {errors.name && <p id={`${id}-name-err`} className="mt-2 text-xs text-primary-600">{errors.name}</p>}
        </div>
        <div className="pt-6">
          <label htmlFor={`${id}-email`} className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700">Email <span className="text-primary-500">*</span></label>
          <input id={`${id}-email`} name="email" type="email" autoComplete="email" placeholder="name@company.com" aria-invalid={!!errors.email} aria-describedby={errors.email ? `${id}-email-err` : undefined} className={`${fieldBase} ${line('email')}`} />
          {errors.email && <p id={`${id}-email-err`} className="mt-2 text-xs text-primary-600">{errors.email}</p>}
        </div>
      </div>

      <div className="pt-6">
        <label htmlFor={`${id}-service`} className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700">Service interest <span className="text-primary-500">*</span></label>
        <div className="relative">
          <select id={`${id}-service`} name="service" defaultValue="" aria-invalid={!!errors.service} className={`${fieldBase} ${line('service')} cursor-pointer appearance-none pr-8`}>
            <option value="" disabled>Select an area</option>
            {services.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            <option value="General inquiry">General inquiry</option>
          </select>
          <span className="pointer-events-none absolute right-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-700"><i className="ri-arrow-down-s-line" /></span>
        </div>
        {errors.service && <p className="mt-2 text-xs text-primary-600">{errors.service}</p>}
      </div>

      <div className="pt-6">
        <div className="flex items-baseline justify-between">
          <label htmlFor={`${id}-message`} className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700">Message <span className="text-primary-500">*</span></label>
          <span className={`font-label text-[11px] ${msgLen > MAX_MSG ? 'text-primary-500' : 'text-foreground-500'}`}>{msgLen}/{MAX_MSG}</span>
        </div>
        <textarea id={`${id}-message`} name="message" rows={4} maxLength={MAX_MSG} onChange={(e) => setMsgLen(e.target.value.length)} placeholder="A few lines about your situation" aria-invalid={!!errors.message} className={`${fieldBase} ${line('message')} resize-none`} />
        {errors.message && <p className="mt-2 text-xs text-primary-600">{errors.message}</p>}
      </div>

      <div className="field-aux">
        <label htmlFor={`${id}-company-alt`}>Company</label>
        <input id={`${id}-company-alt`} name="company_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
      </div>

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button type="submit" disabled={status === 'sending'} className="inline-flex cursor-pointer items-center justify-center gap-3 whitespace-nowrap bg-primary-500 px-7 py-4 font-label text-xs uppercase tracking-[0.14em] text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-wait disabled:opacity-70">
          {status === 'sending' ? 'Sending…' : 'Send inquiry'}
          <i className="ri-arrow-right-line" />
        </button>
        <p className="text-xs leading-relaxed text-foreground-600 sm:max-w-[240px]">Your details are used only to reply to this inquiry.</p>
      </div>

      <div aria-live="polite">
        {status === 'error' && formError && (
          <p role="alert" className="mt-6 border-l-2 border-primary-500 pl-4 text-sm text-primary-700">{formError}</p>
        )}
      </div>
    </form>
  );
}